// IDS (Information Disclosure Statement) Types

export interface IDSFormData {
  appNo: string;
  docketNo: string;
  idsName: string;
  referencesText: string;
  foreignReferencesText: string;
  nplText: string;
  signatureId?: string;
  // Details for the present application, for the top of the form. USPTO has no record
  // for an application that has not published yet, so anything entered here is often the
  // only source. Whatever is filled in wins over the retrieved record.
  filingDate?: string;
  firstInventor?: string;
  artUnit?: string;
  examinerName?: string;
}

export interface IDSSignature {
  id: string;
  name: string;
  registrationNumber: string;
  label?: string;
  // No S-signature text. 37 CFR 1.4(d)(2) requires the signer to insert their own
  // S-signature personally, so no form this tool generates fills that line in.
}

export interface IDSApplicationInfo {
  appNo: string;
  docketNo?: string;
  title?: string;
  inventors?: string[];
  assignee?: string;
  filingDate?: string;
  examiner?: string;
  artUnit?: string;
  confirmationNumber?: string;
}

export interface IDSReference {
  documentNumber: string;
  countryCode: string;
  kind?: string;
  publicationDate?: string;
  patentee?: string;
  relevantPages?: string;
  relevance?: string;
}

export interface IDSNPLReference {
  author?: string;
  title: string;
  publication?: string;
  publicationDate?: string;
  pages?: string;
  relevance?: string;
}

export interface IDSForeignReference {
  documentNumber: string;
  countryCode: string;
  kind?: string;
  publicationDate?: string;
  patentee?: string;
  relevantPages?: string;
  relevance?: string;
}

export interface IDSSubmission {
  idsId: string;
  appNo: string;
  docketNo?: string;
  idsName: string;
  timestamp: string;
  fileKey?: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  usReferencesCount?: number;
  foreignReferencesCount?: number;
  nplReferencesCount?: number;

  // Detailed reference data
  usPatents?: Array<{
    patent_number: string;
    issue_date?: string;
    patentee?: string;
  }>;
  usApplications?: Array<{
    publication_number: string;
    publication_date?: string;
    applicant?: string;
  }>;
  foreignPatents?: Array<{
    country: string;
    docNumber: string;
    kind?: string;
    date?: string;
    patentee?: string;
  }>;
  nonPatentLiterature?: Array<{
    citation: string;
  }>;

  // Application information
  applicationInfo?: {
    filingDate?: string;
    firstInventor?: string;
    artUnit?: string;
    examinerName?: string;
    attorneyDocket?: string;
  };

  // User settings used
  userSettings?: IDSSettings;
}

export interface IDSJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'complete' | 'error' | 'failed';
  idsKey?: string;
  result?: string;
  error_type?: string;
  error_message?: string;
  progress?: number;
}

export interface IDSValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export type SortField = 'document_number' | 'input_order' | 'date' | 'applicant_name';
export type SortDirection = 'asc' | 'desc';

export interface IDSSettings {
  defaultDocketPrefix?: string;
  autoSaveEnabled?: boolean;
  includeForeignRefs?: boolean;
  includeNPLRefs?: boolean;
  maxReferencesPerForm?: number;

  // Sorting preferences
  usPatentsSortField?: SortField;
  usPatentsSortDirection?: SortDirection;
  usPublicationsSortField?: SortField;
  usPublicationsSortDirection?: SortDirection;
  foreignPatentsSortField?: SortField;
  foreignPatentsSortDirection?: SortDirection;

  // Docket number preference
  docketNumberPreference?: 'input' | 'retrieved';

  // Whose name goes in the "Name of Patentee or Applicant" column, per kind of
  // citation. 37 CFR 1.98(b)(1) identifies a US PATENT by its inventor, so that is the
  // default; a published application is as often looked up by its applicant, and a
  // foreign document needs no name at all under 1.98(b)(3) — SB/08 has the column and a
  // firm fills it the way its own back catalogue reads. Hence one choice per kind.
  namePreferences?: {
    usPatents?: NamePreference;
    usPublications?: NamePreference;
    foreignPatents?: NamePreference;
  };
  // The single setting this replaced. Kept so a firm that set it before the preference
  // was per-kind keeps filing the sheet it has been filing: it supplies the value for
  // any kind `namePreferences` does not name.
  patenteePreference?: NamePreference;

  // Signatures
  signatures?: IDSSignature[];
  defaultSignatureId?: string;
}

export type NamePreference = 'inventor' | 'applicant';

/** The kinds of citation whose name column is filled from a record. NPL has none. */
export const NAME_PREFERENCE_KINDS = [
  {
    key: 'usPatents',
    label: 'US patents',
    hint: '37 CFR 1.98(b)(1) identifies a US patent by its inventor; examiners have returned corrected sheets listing only the assignee.',
  },
  {
    key: 'usPublications',
    label: 'US publications',
    hint: 'A published application is as often looked up by its applicant as by its first named inventor.',
  },
  {
    key: 'foreignPatents',
    label: 'Foreign patents',
    hint: '37 CFR 1.98(b)(3) requires no name for a foreign document — country, number and date identify it — but SB/08 has the column and an examiner reads it.',
  },
] as const;

export interface IDSPatentFamily {
  pubNo: string;
  members: Array<{
    countryCode: string;
    documentNumber: string;
    kind?: string;
    publicationDate?: string;
  }>;
  status?: 'TOO_BIG' | 'success';
}

// ---------------------------------------------------------------- already of record

/** One reference the application has already cited, and where it was cited. */
/** Where the knowledge that a reference is cited came from. */
export type OfRecordSource = 'wrapper' | 'user';

export interface OfRecordReference {
  /** "wrapper" — read from an IDS or PTO-892 in the file. "user" — recorded by a filer
   *  when they generated an IDS, before the wrapper shows it. */
  source?: OfRecordSource;
  /** Set on a user-recorded entry: which IDS it was filed on. */
  idsName?: string;
  addedAt?: string;
  /** Normalised — commas and kind codes removed — so two spellings compare equal. */
  id: string;
  /** As a person writes it. */
  display: string;
  /** The line of the form it was read from, for checking against the paper. */
  line: string;
  sources: {
    documentCode: string;
    mailDate: string;
    documentIdentifier: string;
    /** "applicant" for an IDS, "examiner" for a PTO-892. */
    citedBy: 'applicant' | 'examiner';
  }[];
}

/**
 * What an application has already cited, read from its file wrapper.
 *
 * `npl` has no document number to match on, so it is the lines the citations were found
 * on — for a person to read rather than for the tool to compare against.
 */
export interface IdsOfRecord {
  applicationNumber: string;
  usPatents: OfRecordReference[];
  usPublications: OfRecordReference[];
  foreignPatents: OfRecordReference[];
  npl: OfRecordReference[];
  counts: {
    usPatents: number;
    usPublications: number;
    foreignPatents: number;
    npl: number;
  };
  /** The IDS and PTO-892 papers that were read, and how many references each carried. */
  sources: {
    documentCode: string;
    mailDate: string;
    documentIdentifier: string;
    citedBy: 'applicant' | 'examiner';
    references: number;
  }[];
  /** Papers that could not be read. Named, because a short list you trust is worse
   *  than a short list you know is short. */
  unreadable: { documentCode: string; mailDate: string; documentIdentifier: string }[];
  documentsRead: number;
}


// ------------------------------------------------- prior art read for its numbers

/** What kind of citation an uploaded document turned out to be. */
export type ExtractedKind =
  | 'us_patent'
  | 'us_publication'
  | 'foreign_patent'
  | 'npl'
  | 'not_found';

/**
 * One uploaded prior-art document, as the extraction read it.
 *
 * A document that could not be identified is still returned, with `status` saying why.
 * A filer who uploads ten files and is shown nine results has no way to tell which one
 * the tool never managed to read.
 */
export interface ExtractedArtDocument {
  id: string;
  /** The filename as uploaded, so the filer can tell which of their documents this is. */
  name: string;
  kind: ExtractedKind;
  /** Formatted for the form: digits for a US patent, the full number for a publication,
   *  country+number+kind for a foreign document, a full citation for NPL. */
  value: string;
  date?: string;
  party?: string;
  /** Which page it was found on. 0 when it was not found. */
  page: number;
  status: 'found' | 'not_found' | 'unreadable';
}

export interface ExtractedArt {
  documents: ExtractedArtDocument[];
  counts: { found: number; notFound: number; unreadable: number; total: number };
  /** Which form field each kind belongs in, supplied by the extraction rather than
   *  duplicated here. */
  fieldOfKind: Record<string, string>;
}

/** One stored upload, as the upload endpoint reports it back. */
export interface UploadedArtKey {
  id: string;
  name: string;
  key: string;
}
